import React, { useEffect, useState } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Button from '@material-ui/core/Button'
import FormHelperText from '@material-ui/core/FormHelperText'
import InfoAlert from 'components/info-alert'
import CurrencyField from 'components/form-util/currency'
import AccountRouterLink from 'components/account-router-link'
import { apolloClient } from 'lib/graphql'
import { buildDateTime } from 'lib/CustomLuxonUtils'
import { centsToDollarsFormatted } from 'lib/utils/number'
import pluralize from 'lib/pluralize'
import uniq from 'lodash/uniq'
import PaymentAccountWarning from 'components/payment-account-warning'
import ManageStripeAccountConfirmationDialog from 'pages/account/payments/payment-accounts/manage-stripe-account-confirmation-dialog'
import {
  GROUP_EVENT_COST,
  GROUP_EVENT_PAYMENT_OPTIONS,
} from 'components/group-events/create-group-event-form-wizard/queries'
import {
  isWeeklyRecurringPayment,
  isMonthlyRecurringPayment,
  isOnlinePayment,
  isRecurringPayment,
  isManualPayment,
  MANUAL_PAYMENT,
} from 'utils/groupEventUtils'
import { useStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'

function generateMappedSessions({ sessions, selectedFacility }) {
  try {
    return sessions.map(session => ({
      facilityId: selectedFacility.id,
      startDateTime: buildDateTime(
        session.startDate,
        session.startTime,
        selectedFacility.timezone,
      ),
      endDateTime: buildDateTime(
        session.startDate,
        session.endTime,
        selectedFacility.timezone,
      ),
    }))
  } catch (error) {
    return []
  }
}

function serializeSessions({ sessions, selectedFacility }) {
  if (sessions && selectedFacility) {
    return JSON.stringify(
      generateMappedSessions({ sessions, selectedFacility }),
    )
  }
}

const fetchCalculateGroupEventCost = async ({
  priceInCents,
  paymentRecurrenceSchedule,
  serializedSessionsJSON,
}) => {
  const mappedSessions = JSON.parse(serializedSessionsJSON)

  const { data } = await apolloClient.query({
    query: GROUP_EVENT_COST,
    variables: {
      priceInCents,
      paymentRecurrenceSchedule,
      sessions: mappedSessions,
    },
    fetchPolicy: 'cache-first',
  })

  return data.groupEventCost
}

const fetchGroupEventPaymentOptions = async serializedSessionsJSON => {
  const mappedSessions = JSON.parse(serializedSessionsJSON)

  if (mappedSessions.length === 0) {
    return []
  }

  const { data } = await apolloClient.query({
    query: GROUP_EVENT_PAYMENT_OPTIONS,
    variables: {
      sessions: mappedSessions,
    },
    fetchPolicy: 'cache-first',
  })

  return data.groupEventPaymentOptions
}

function paymentCalculationLabel({
  price,
  calculatedNumberOfPayments,
  paymentRecurrenceSchedule,
}) {
  const formattedPrice = centsToDollarsFormatted(price * 100)
  const isWeeklyRecurring = isWeeklyRecurringPayment({
    paymentRecurrenceSchedule,
  })
  const isMonthlyRecurring = isMonthlyRecurringPayment({
    paymentRecurrenceSchedule,
  })
  const paymentSuffix = pluralize(calculatedNumberOfPayments - 1, 'payment')
  const anniversarySuffix = isMonthlyRecurring
    ? ' on the anniversary of the first session'
    : ''
  const paymentType = isWeeklyRecurring ? 'weekly' : 'monthly'
  const additionalPayments = calculatedNumberOfPayments - 1

  return `${formattedPrice} due at registration, followed by ${additionalPayments} additional ${paymentType} ${paymentSuffix}${anniversarySuffix}.`
}

const pricingLabel = paymentRecurrenceSchedule => {
  if (isWeeklyRecurringPayment({ paymentRecurrenceSchedule })) {
    return 'Price (per week) *'
  }
  if (isMonthlyRecurringPayment({ paymentRecurrenceSchedule })) {
    return 'Price (per month) *'
  }
  return 'Price (per person) *'
}

const feeLabel = paymentRecurrenceSchedule => {
  if (isRecurringPayment({ paymentRecurrenceSchedule })) {
    return (
      <>
        Please note that a 2.9% + $0.30 fee will be applied to{' '}
        <strong>each</strong> billing cycle transaction.
      </>
    )
  }

  return (
    <>
      Please note that a 2.9% + $0.30 fee will be applied to the transaction
      total.
    </>
  )
}

const getManualPaymentOption = options =>
  options.find(option => option.value === MANUAL_PAYMENT) ?? {
    value: MANUAL_PAYMENT,
    name: 'Manually Collect Payment',
  }

const CreateEventPayment = ({
  paymentAccounts,
  coachFacilities,
  coachAcademies,
}) => {
  const classes = useStyles()
  const [isManageStripeDialogOpen, setIsManageStripeDialogOpen] = useState(
    false,
  )
  const {
    errors,
    control,
    watch,
    register,
    setValue,
    getValues,
  } = useFormContext()
  const tags = getValues('tags')
  const isPgaHopeEvent = tags?.includes('PGA HOPE')

  const academyFacilities = coachAcademies.reduce(
    (acc, academy) => [...acc, ...academy.facilities],
    [],
  )
  const allFacilities = uniq(coachFacilities.concat(academyFacilities))

  const isExternalRegistration = watch('isExternalRegistration')
  const paymentRecurrenceSchedule = watch('paymentRecurrenceSchedule')
  const sessions = watch('sessions')
  const price = watch('price')
  const locationId = watch('locationId')

  const selectedPaymentAccountId = watch('paymentAccountId')
  const selectedAccount = paymentAccounts.find(
    acc => acc.id === selectedPaymentAccountId,
  )
  const hasIncompleteStripeSetup =
    selectedAccount &&
    (!selectedAccount.detailsSubmitted || !selectedAccount.chargesEnabled)

  const selectedFacility = allFacilities.find(f => f.id === locationId)
  const serializedSessionsJSON = serializeSessions({
    sessions,
    selectedFacility,
  })

  const [
    calculatedTotalPriceInCents,
    setCalculatedTotalPriceInCents,
  ] = useState(null)
  const [calculatedNumberOfPayments, setCalculatedNumberOfPayments] = useState(
    null,
  )
  const [paymentOptions, setPaymentOptions] = useState([])

  useEffect(() => {
    ;(async () => {
      if (serializedSessionsJSON) {
        const groupEventPaymentOptions = await fetchGroupEventPaymentOptions(
          serializedSessionsJSON,
        )
        setPaymentOptions(groupEventPaymentOptions)
      } else {
        setPaymentOptions([])
      }
    })()
  }, [serializedSessionsJSON])

  useEffect(() => {
    ;(async () => {
      const priceInCents = parseInt(
        isNaN(parseFloat(price)) ? 0 : parseFloat(price) * 100,
      )

      if (
        serializedSessionsJSON &&
        priceInCents > 0 &&
        isRecurringPayment({ paymentRecurrenceSchedule })
      ) {
        try {
          const groupEventCost = await fetchCalculateGroupEventCost({
            priceInCents,
            paymentRecurrenceSchedule,
            serializedSessionsJSON,
          })

          setCalculatedTotalPriceInCents(groupEventCost.totalPriceInCents)
          setCalculatedNumberOfPayments(groupEventCost.numberOfPayments)
        } catch (error) {
          setCalculatedTotalPriceInCents(null)
          setCalculatedNumberOfPayments(null)
        }
      } else {
        setCalculatedTotalPriceInCents(null)
        setCalculatedNumberOfPayments(null)
      }
    })()
  }, [paymentRecurrenceSchedule, price, serializedSessionsJSON])

  useEffect(() => {
    if (isPgaHopeEvent) {
      setValue('paymentRecurrenceSchedule', MANUAL_PAYMENT)
      setValue('price', 0)
    }
  }, [isPgaHopeEvent, setValue])

  useEffect(() => {
    if (
      isExternalRegistration &&
      !isManualPayment({ paymentRecurrenceSchedule })
    ) {
      setValue('paymentRecurrenceSchedule', MANUAL_PAYMENT)
    }
  }, [isExternalRegistration, paymentRecurrenceSchedule, setValue])

  const displayPaymentOptions = isExternalRegistration
    ? [getManualPaymentOption(paymentOptions)]
    : paymentOptions

  if (isPgaHopeEvent) return null

  return (
    <>
      <Box className={classes.fieldGroup}>
        <Typography className={classes.sectionHeading}>
          Payment collection
        </Typography>
        <FormControl
          error={!!errors.paymentRecurrenceSchedule}
          variant="outlined"
          fullWidth
          className={classes.fieldInput}
        >
          <InputLabel id="payment-recurrence-schedule-label">
            Payment collection *
          </InputLabel>
          <Controller
            defaultValue=""
            as={
              <Select
                labelId="payment-recurrence-schedule-label"
                label="Payment collection *"
                disabled={isExternalRegistration}
                defaultValue=""
                data-cy="payment-recurrence-schedule"
              >
                {displayPaymentOptions.map(paymentOption => (
                  <MenuItem
                    key={paymentOption.value}
                    value={paymentOption.value}
                  >
                    {paymentOption.name}
                  </MenuItem>
                ))}
              </Select>
            }
            name="paymentRecurrenceSchedule"
            control={control}
          />
          {errors?.paymentRecurrenceSchedule ? (
            <FormHelperText error>
              {errors.paymentRecurrenceSchedule.message}
            </FormHelperText>
          ) : null}
        </FormControl>
      </Box>
      <Box className={classes.fieldGroup}>
        <CurrencyField
          name="price"
          label={pricingLabel(paymentRecurrenceSchedule)}
          inputRef={register}
          error={!!errors.price}
          helperText={errors?.price?.message}
          className={classes.fieldInput}
        />
        {isRecurringPayment({ paymentRecurrenceSchedule }) ? (
          <>
            <Typography
              className={classes.fieldHelper}
              style={{ marginTop: 8 }}
            >
              Total cost:{' '}
              {centsToDollarsFormatted(calculatedTotalPriceInCents || 0)}
            </Typography>
            {calculatedTotalPriceInCents && calculatedNumberOfPayments ? (
              <Typography className={classes.fieldHelper}>
                {paymentCalculationLabel({
                  price,
                  calculatedNumberOfPayments,
                  paymentRecurrenceSchedule,
                })}
              </Typography>
            ) : null}
          </>
        ) : null}
      </Box>
      {isOnlinePayment({ paymentRecurrenceSchedule }) ? (
        <Box className={classes.fieldGroup}>
          <Typography className={classes.fieldHelper}>
            Select a payment account to collect registration fees online for
            this event.
          </Typography>
          <FormControl
            error={!!errors.paymentAccountId || hasIncompleteStripeSetup}
            variant="outlined"
            fullWidth
            className={classes.fieldInput}
          >
            <InputLabel id="create-event-payment-account-label">
              Payment account *
            </InputLabel>
            <Controller
              defaultValue=""
              name="paymentAccountId"
              control={control}
              as={
                <Select
                  labelId="create-event-payment-account-label"
                  label="Payment account *"
                  value=""
                  defaultValue=""
                  data-cy="payment-account"
                >
                  {paymentAccounts.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              }
            />
            {errors?.paymentAccountId ? (
              <FormHelperText error>
                {errors.paymentAccountId.message}
              </FormHelperText>
            ) : null}
          </FormControl>
          <Box mt={1}>
            <Button
              color="primary"
              size="small"
              to="/account/payments/payment-accounts"
              component={AccountRouterLink}
            >
              Manage payment accounts
            </Button>
          </Box>
          <PaymentAccountWarning
            paymentAccount={selectedAccount}
            hasUnresolvedPaymentAccount={
              !!selectedPaymentAccountId && !selectedAccount
            }
            onOpen={() => setIsManageStripeDialogOpen(true)}
          />
          <InfoAlert mt={2}>{feeLabel(paymentRecurrenceSchedule)}</InfoAlert>
        </Box>
      ) : null}
      <ManageStripeAccountConfirmationDialog
        paymentAccount={selectedAccount}
        isOpen={isManageStripeDialogOpen}
        onClose={() => setIsManageStripeDialogOpen(false)}
      />
    </>
  )
}

export default CreateEventPayment
